import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import SEO from "@/components/app/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function scorePassword(pw) {
  let s = 0;
  if (pw.length >= 12) s += 1;
  if (/[A-Z]/.test(pw)) s += 1;
  if (/[a-z]/.test(pw)) s += 1;
  if (/\d/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  return s; // 0..5
}

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const score = useMemo(() => scorePassword(pw), [pw]);
  const match = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (score < 5) {
      toast.error("Mot de passe trop faible (12 caractères, 1 majuscule, 1 minuscule, 1 chiffre, 1 spécial).");
      return;
    }
    if (!match) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, { token, password: pw });
      toast.success("Votre mot de passe a été modifié.");
      nav("/connexion", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lien invalide ou expiré.");
    } finally {
      setLoading(false);
    }
  };

  const strengthColor =
    score <= 1 ? "bg-red-500"
      : score <= 2 ? "bg-orange-500"
      : score <= 3 ? "bg-yellow-500"
      : score <= 4 ? "bg-lime-500"
      : "bg-green-600";

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="card-elevated p-6 sm:p-8 text-center">
          <h1 className="text-2xl font-bold mb-3">Lien invalide</h1>
          <p className="text-sm text-muted-foreground mb-6">Ce lien de réinitialisation est incomplet ou a expiré.</p>
          <Button asChild variant="outline" className="w-full">
            <Link to="/mot-de-passe-oublie" data-testid="request-new-link">Demander un nouveau lien</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <SEO title="Réinitialiser mon mot de passe" description="Choisissez un nouveau mot de passe." canonical="/reinitialiser-mot-de-passe" noindex />
      <div className="card-elevated p-6 sm:p-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">Nouveau mot de passe</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Choisissez un mot de passe fort. Minimum 12 caractères, avec majuscules, minuscules, chiffres et caractères spéciaux.
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label htmlFor="rp-pw">Nouveau mot de passe</Label>
            <div className="relative">
              <Input
                id="rp-pw"
                data-testid="reset-pw-1"
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                autoComplete="new-password"
                className="text-base pr-10"
              />
              <button
                type="button"
                aria-label={show ? "Masquer" : "Afficher"}
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="toggle-pw-visibility"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div className={`h-full ${strengthColor} transition-all duration-300`} style={{ width: `${(score / 5) * 100}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-1" data-testid="reset-strength-hint">
              Robustesse&nbsp;: {["Très faible", "Faible", "Moyen", "Bien", "Fort", "Très fort"][score]}
            </p>
          </div>

          <div>
            <Label htmlFor="rp-pw2">Confirmer le mot de passe</Label>
            <Input
              id="rp-pw2"
              data-testid="reset-pw-2"
              type={show ? "text" : "password"}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              autoComplete="new-password"
              className="text-base"
            />
            {pw2 && !match && <p className="text-xs text-red-600 mt-1">Les deux mots de passe ne correspondent pas.</p>}
          </div>

          <Button
            type="submit"
            disabled={loading || score < 5 || !match}
            className="w-full bg-gradient-brand text-white border-0 hover:opacity-90"
            data-testid="reset-submit-btn"
          >
            {loading ? "Enregistrement..." : "Choisir ce mot de passe"}
          </Button>
          <p className="text-center text-sm">
            <Link to="/connexion" className="text-muted-foreground hover:text-foreground">← Retour à la connexion</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
